// /settings → "Мэдэгдлийн загвар" tab (A7) with §17/B6 inline CRUD + preview.

import {
  Badge,
  Box,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { EyeIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { NotificationTemplateApi } from "../../apis"
import type { INotificationTemplate, NotificationChannel } from "../../apis/notification-template"
import RenderText from "../ui/render-text"
import { NotificationPreviewModal } from "./NotificationPreviewModal"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsActionCell, SettingsButton, SettingsRowAction } from "./settings-controls"
import { NotificationTemplateFormModal } from "./NotificationTemplateFormModal"
import { useSettingsMutations } from "./hooks"
import { DotPill } from "../delivery/atoms"

export function NotificationTemplateTab() {
  const { t } = useTranslation("settings")
  const { data, isLoading, error, mutate: reload } = useSWR(
    "settings.notification-template.list",
    () => NotificationTemplateApi.list({ offset: { limit: 100, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const rows = data?.rows ?? []
  const mutations = useSettingsMutations()

  const [editTarget, setEditTarget] = useState<INotificationTemplate | null>(null)
  const [previewTarget, setPreviewTarget] = useState<INotificationTemplate | null>(null)

  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const [previewOpen, { open: openPreview, close: closePreview }] = useDisclosure(false)

  const handleEdit = (row: INotificationTemplate) => {
    setEditTarget(row)
    openEdit()
  }

  const handlePreview = (row: INotificationTemplate) => {
    setPreviewTarget(row)
    openPreview()
  }

  const handleDelete = (row: INotificationTemplate) => {
    modals.openConfirmModal({
      title: t("common.confirmDelete", { name: row.templateKey }),
      children: (
        <Text size="sm">
          <Text span fw={600}>{row.templateKey}</Text>{" "}
          {t("common.delete").toLowerCase()}?
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => mutations.notifyTemplate.remove(row.id),
    })
  }

  const channelLabel = (ch: string) =>
    t(`notification.options.channel.${ch as NotificationChannel}`)

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Stack gap={2}>
          <Title order={4}>{t("notification.title")}</Title>
          <Text size="sm" c="gray.7">
            {t("notification.fields.body.description")}
          </Text>
        </Stack>
        <SettingsButton code={SETTINGS_WRITE.governance} leftSection={<PlusIcon size={18} />} onClick={openCreate}>
          {t("notification.actions.newTemplate")}
        </SettingsButton>
      </Group>

      {isLoading ? (
        <ListState state="loading" columns={6} />
      ) : error ? (
        <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={() => reload()} />
      ) : rows.length === 0 ? (
        <ListState state="empty" title={t("common.empty")} description={t("common.emptyHint")} />
      ) : (
        <Table striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={160}>{t("common.actions")}</Table.Th>
              <Table.Th>{t("notification.fields.templateKey.label")}</Table.Th>
              <Table.Th>{t("notification.fields.channel.label")}</Table.Th>
              <Table.Th>{t("notification.fields.language.label")}</Table.Th>
              <Table.Th>{t("notification.fields.subject.label")}</Table.Th>
              <Table.Th>{t("notification.fields.isActive.label")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <SettingsActionCell>
                    <SettingsRowAction code="BIZ_GOV_VIEW" icon={<EyeIcon />} label={t("common.preview")} onClick={() => handlePreview(r)} />
                    <SettingsRowAction code={SETTINGS_WRITE.governance} icon={<PencilSimpleIcon />} label={t("common.edit")} onClick={() => handleEdit(r)} />
                    <SettingsRowAction code={SETTINGS_WRITE.governance} icon={<TrashIcon />} label={t("common.delete")} onClick={() => handleDelete(r)} />
                  </SettingsActionCell>
                </Table.Td>
                <Table.Td>
                  <RenderText text={t(`notification.templateKeys.${r.templateKey}`, { defaultValue: r.templateKey })} fontWeight={500} />
                </Table.Td>
                <Table.Td>
                  <Badge size="lg" variant="light" color="gray" tt="none">
                    {channelLabel(r.channel)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <RenderText text={r.language} color="dimmed" />
                </Table.Td>
                <Table.Td>
                  <RenderText text={r.subject ?? "-"} color="dimmed" />
                </Table.Td>
                <Table.Td>
                  <DotPill tone={r.isActive ? "green" : "grey"}>
                    {r.isActive ? t("common.active") : t("common.inactive")}
                  </DotPill>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <NotificationTemplateFormModal
        opened={createOpen}
        onClose={closeCreate}
      />
      <NotificationTemplateFormModal
        opened={editOpen}
        onClose={closeEdit}
        initial={editTarget}
        key={`edit-${editTarget?.id ?? "none"}`}
      />
      <NotificationPreviewModal
        opened={previewOpen}
        onClose={closePreview}
        template={previewTarget}
      />
    </Box>
  )
}
